import { useHostElement } from "#ce/composables/useHostElement";

/**
 * Expose properties and methods to the host element.
 * Properties and methods are applied immediately since the host is available synchronously.
 *
 * @returns {Object}
 * @property {Function} exposeProperties
 * @property {Function} exposeMethods
 */
export function useExpose() {
  const host = useHostElement();

  function exposeProperties(properties) {
    if (!host) {
      return;
    }

    for (const [key, descriptor] of Object.entries(properties)) {
      // Capture any value that was set on the element before the accessor is installed.
      // This happens when external code (e.g. jQuery.val()) sets a plain data property
      // before the Vue component has had a chance to define the accessor.
      const existingValue = host[key];

      // Configurable by default, so a later mount can install over this one. A host that
      // disconnects long enough to unmount runs setup again on re-insertion, and a
      // non-configurable accessor would leave the second install closed over the dead instance.
      define(key, { configurable: true, ...descriptor });

      if (existingValue !== undefined && descriptor.set) {
        descriptor.set(existingValue);
      }
    }
  }

  function exposeMethods(methods) {
    if (!host) {
      return;
    }

    for (const [key, value] of Object.entries(methods)) {
      // Not enumerable, matching the platform: a method on an element is not part of its own
      // enumerable keys, so `for…in` and spread over the host do not carry it.
      define(key, { configurable: true, writable: true, enumerable: false, value });
    }
  }

  // `Reflect.defineProperty` reports a refused definition by returning false rather than throwing,
  // so an unchecked call leaves a host member that silently does not exist.
  function define(key, descriptor) {
    if (Reflect.defineProperty(host, key, descriptor)) {
      return;
    }

    throw new Error(
      `<${host.localName}> cannot expose \`${key}\`: the host already carries a non-configurable ` +
        `property under that name. Most often that is a declared prop — Vue installs one accessor ` +
        `per prop, so a live IDL property cannot share the name. Read the attribute through ` +
        `useAttrs() and keep the name here.`,
    );
  }

  return {
    /**
     * Expose properties to the host element.
     *
     * @param {Object} properties
     * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/defineProperties
     *
     * @example
     * exposeProperties({
     *  property1: {
     *   value: "value1",
     *  },
     *  property2: {
     *   get: () => "value2",
     *   set: (value) => [...]
     *  },
     * });
     */
    exposeProperties,

    /**
     * Expose methods to the host element.
     *
     * @param {Object} methods
     * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/assign
     *
     * @example
     * exposeMethods({
     *  method1() {
     *   console.log("method1");
     *  },
     *  method2() {
     *   console.log("method2");
     *  },
     * });
     */
    exposeMethods,
  };
}
